import { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  Text,
  UseDisclosureReturn,
} from '@frontend/components';

import {
  groupMemberChangeUserGroup,
  superUserChangeUserGroup,
} from 'modules/common/userGroupManagement/data/actions';

import { useAppSelector } from 'store/hooks';
import {
  getAvailableGroupMembers,
  getIsSuperuser,
} from 'store/session/selectors';

type ConfirmChangeSiteDialogProps = UseDisclosureReturn & {
  groupBusinessName: string;
  userGroupId: number;
  nextUrl: string;
};

export const ConfirmChangeSiteDialog = ({
  isOpen,
  onClose,
  groupBusinessName,
  nextUrl,
  userGroupId,
}: ConfirmChangeSiteDialogProps) => {
  const isSuperUser = useSelector(getIsSuperuser);
  const availableGroupMembers = useAppSelector(getAvailableGroupMembers);

  const groupIdToMemberMap = useMemo(
    () =>
      Object.fromEntries(
        availableGroupMembers.map((member) => [member.group.id, member]),
      ),
    [availableGroupMembers],
  );

  const dispatch = useDispatch();
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  const handleSwitch = async () => {
    const groupMemberId = groupIdToMemberMap[userGroupId]?.id;
    if (isSuperUser && userGroupId) {
      dispatch(
        superUserChangeUserGroup.request({
          userGroupId,
          nextUrl,
        }),
      );
    } else if (groupMemberId) {
      dispatch(
        groupMemberChangeUserGroup.request({
          groupMemberId,
          nextUrl,
        }),
      );
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={cancelRef}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Change active site?</AlertDialogHeader>
          <AlertDialogBody>
            <Text>
              Following this link will change your active site to{' '}
              <Text as="span" fontWeight="bold">
                {groupBusinessName}
              </Text>{' '}
              across any open tabs and windows. Any unsaved changes will be
              lost.
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <ButtonGroup>
              <Button ref={cancelRef} variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSwitch}>Proceed</Button>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
